import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useQueryPagination from 'hooks/useQueryPagination'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { Flex, H1WithBack, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText } from 'styling/Typography'
import { convertToDate, formatNumbersForDisplay, getParams, handlePagination } from 'utils/helpers'
import { clearFuelData, getBatches } from 'features/fuelManagement/fuelManagementSlice'
import { getFuelCustodyPricing } from 'features/fuelManagement/fuelCustodySlice'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { FilterEnum, IFilter } from 'components/Filter/SingleFilter'
import { fuelTypes } from './fuelData'
import { endOfWeek, startOfWeek } from 'date-fns'
import { IFuelDeliveryRecord } from 'types/fuelDeliveries.interface'
import { formatGhs, stockValuationLine } from './Shared/fuelFormatters'
import { mapBatchToDelivery } from './Shared/fuelApiMappers'
import { DeliveryDetailModal, DELIVERY_VERIFICATION_META } from './DeliveryDetailModal'

const columns: IHonestTableColumn<IFuelDeliveryRecord>[] = [
  { id: 'date', header: 'DATE', cell: (item) => item.dateLabel },
  { id: 'tank', header: 'TANK', cell: (item) => item.tank },
  { id: 'type', header: 'TYPE', cell: (item) => item.fuelType },
  {
    id: 'quantity',
    header: 'QUANTITY',
    cell: (item) => <BodyText weight='bold'>{`${formatNumbersForDisplay(item.liters)} L`}</BodyText>,
  },
  {
    id: 'price',
    header: 'PRICE / L',
    cell: (item) => (item.pricePerL == null ? '—' : `GH₵ ${item.pricePerL.toFixed(2)}`),
  },
  { id: 'cost', header: 'COST', cell: (item) => (item.costGhs ? formatGhs(item.costGhs) : '/') },
  { id: 'supplier', header: 'SUPPLIER', cell: (item) => item.supplier },
  { id: 'employee', header: 'EMPLOYEE', cell: (item) => item.receivedBy || '/' },
  {
    id: 'verification',
    header: 'VERIFICATION',
    cell: (item) => {
      const meta = DELIVERY_VERIFICATION_META[item.verificationStatus]
      return (
        <StatusPill status={meta.tone} dot={item.verificationStatus === 'SHORT'}>
          {meta.label}
        </StatusPill>
      )
    },
  },
]

export const FuelBatches = () => {
  const dispatch = useDispatch()
  const { batches } = useSelector((store: RootState) => store.fuelManagement)
  const { pricing } = useSelector((store: RootState) => store.fuelCustody)
  const searchParams = new URLSearchParams(window.location.search)
  const [params, setParams] = useState<any>(getParams({
    location, ap: {
      dateFrom: searchParams.get('dateFrom') ? new Date(searchParams.get('dateFrom')) : startOfWeek(new Date(), { weekStartsOn: 1 }),
      dateTo: searchParams.get('dateTo') ? new Date(searchParams.get('dateTo')) : endOfWeek(new Date(), { weekStartsOn: 1 }),
    },
  }))
  const [selectedDelivery, setSelectedDelivery] = useState<IFuelDeliveryRecord | null>(null)

  useQueryPagination({ fetchFunctions: [getBatches], cleanUpFunctions: [clearFuelData], params })

  useEffect(() => {
    dispatch(getFuelCustodyPricing() as any)
  }, [dispatch])

  const rows: IFuelDeliveryRecord[] = useMemo(
    () => (batches?.data?.length ? batches.data.map(mapBatchToDelivery) : []),
    [batches],
  )

  const valuationLine = pricing
    ? stockValuationLine(pricing.wacPerLitre, pricing.lastDeliveryPricePerLitre)
    : ''

  const setDateFrom = (value) => {
    setParams((prev) => {
      const copy = structuredClone(prev)
      copy.dateFrom = convertToDate(value)
      return copy
    })
  }

  const setDateTo = (value) => {
    setParams((prev) => {
      const copy = structuredClone(prev)
      copy.dateTo = convertToDate(value)
      return copy
    })
  }

  const filters: IFilter[] = [
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Fuel Type',
      state: null,
      propName: 'fuelTypeId',
      sd: true,
      single: true,
      filterOptions: fuelTypes.map((s) => ({ name: s.label, id: s.value.toString() })),
    },
  ]

  return (
    <>
      <NavBar filterOptions={filters} enableTemporaryManualClosing setParams={setParams} params={params} />
      <PageWrapper>
        <Flex width='100%' between>
          <Flex column gap='0.25rem'>
            <H1WithBack title='Batches' arrow />
            {valuationLine && <OtherText grey>{valuationLine}</OtherText>}
          </Flex>
          <Flex gap='1rem'>
            <CalendarInput
              maxDate={new Date(params.dateTo)}
              labelText='Start date'
              defaultValue={new Date(params.dateFrom || new Date())}
              onChange={(v) => setDateFrom(v)}
            />
            <CalendarInput
              minDate={new Date(params.dateFrom)}
              labelText='End date'
              defaultValue={new Date(params.dateTo || new Date())}
              onChange={(v) => setDateTo(v)}
            />
          </Flex>
        </Flex>
        {!rows.length ? (
          <NoItems title='Batches' />
        ) : (
          <HonestTable
            columns={columns}
            data={rows}
            keyField='id'
            onClick={(item) => setSelectedDelivery(item)}
          />
        )}
        {selectedDelivery && (
          <DeliveryDetailModal
            delivery={selectedDelivery}
            closeModal={() => setSelectedDelivery(null)}
          />
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(batches?.totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
